import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import fixWebmDuration from 'fix-webm-duration';

export type RecordingPhase = 'idle' | 'preview' | 'recording' | 'review';

export interface UseVideoRecorder {
  phase: RecordingPhase;
  elapsedSeconds: number;
  recordedUrl: string | null;
  recordedFile: File | null;
  permissionError: string | null;
  isSupported: boolean;
  liveVideoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  beginPreview: () => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
  retake: () => Promise<void>;
  cancel: () => void;
}

const MAX_DURATION_SECONDS = 60;
const CAPTURE_FPS = 30;

// Recording happens off a <canvas>.captureStream() rather than the camera
// stream directly. Routing camera frames through a canvas (a) guarantees the
// MediaRecorder receives a stream with valid, well-formed video tracks
// (some webcams + Chrome MediaRecorder combos emit malformed 2x2 blobs when
// fed the camera stream directly) and (b) is the same pipeline we'll need
// when MediaPipe SelfieSegmentation is added.
//
// Order matters: WebM is preferred where supported because Chrome's MP4
// MediaRecorder (introduced in v130) emits files with broken container
// metadata. Safari has no WebM MediaRecorder support, so it correctly falls
// through to its native (and well-formed) MP4 path.
const MIME_CANDIDATES: Array<{ mime: string; ext: string }> = [
  { mime: 'video/webm;codecs=vp9,opus', ext: 'webm' },
  { mime: 'video/webm;codecs=vp8,opus', ext: 'webm' },
  { mime: 'video/webm', ext: 'webm' },
  { mime: 'video/mp4;codecs=avc1,mp4a', ext: 'mp4' },
  { mime: 'video/mp4', ext: 'mp4' },
];

const pickSupportedMime = (): { mime: string; ext: string } | null => {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate.mime)) return candidate;
    } catch {
      // ignore — some browsers throw on unknown MIME
    }
  }
  return null;
};

export const useVideoRecorder = (): UseVideoRecorder => {
  const [phase, setPhase] = useState<RecordingPhase>('idle');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);

  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const recordedUrlRef = useRef<string | null>(null);

  const supported = useMemo(() => pickSupportedMime(), []);
  const isSupported = supported !== null;

  const stopDrawLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
  }, []);

  const stopCanvasStream = useCallback(() => {
    const cs = canvasStreamRef.current;
    if (cs) {
      cs.getTracks().forEach((t) => t.stop());
    }
    canvasStreamRef.current = null;
  }, []);

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const revokeRecordedUrl = useCallback(() => {
    if (recordedUrlRef.current) {
      URL.revokeObjectURL(recordedUrlRef.current);
      recordedUrlRef.current = null;
    }
  }, []);

  const startDrawLoop = useCallback(() => {
    stopDrawLoop();
    const draw = () => {
      const video = liveVideoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.videoWidth > 0 && video.videoHeight > 0) {
        if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
        if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Mirror the front camera horizontally so the live preview and
          // the recording look natural to the user (selfies match
          // expectations from native camera apps).
          ctx.save();
          ctx.scale(-1, 1);
          ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
          ctx.restore();
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
  }, [stopDrawLoop]);

  const acquireStream = useCallback(async (): Promise<boolean> => {
    setPermissionError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionError('This browser does not support camera capture.');
      return false;
    }
    if (!supported) {
      setPermissionError('This browser does not support video recording.');
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: true,
      });
      streamRef.current = stream;
      const video = liveVideoRef.current;
      if (video) {
        video.srcObject = stream;
        // Kick the draw loop both ways: immediately (the rAF body guards on
        // video.videoWidth > 0, so early calls are no-ops until frames
        // arrive) AND on loadedmetadata for the cold-start case. The
        // immediate call matters for the second+ record session: the
        // <video> still has metadata from the previous stream, so
        // loadedmetadata may not refire when we swap srcObject, leaving
        // the draw loop dormant and the canvas blank.
        startDrawLoop();
        video.onloadedmetadata = () => {
          startDrawLoop();
        };
        try {
          await video.play();
        } catch {
          // ignore — draw loop will resume once frames are ready
        }
      }
      return true;
    } catch (err) {
      const e = err as DOMException;
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
        setPermissionError(
          'Camera access blocked — enable it in your browser settings, then cancel and try again.',
        );
      } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
        setPermissionError('No camera was found on this device.');
      } else if (e?.name === 'NotReadableError') {
        setPermissionError('Camera is already in use by another application.');
      } else {
        setPermissionError('Could not start the camera. Please try again.');
      }
      return false;
    }
  }, [startDrawLoop, supported]);

  const beginPreview = useCallback(async () => {
    if (phase !== 'idle') return;
    setPhase('preview');
    const ok = await acquireStream();
    if (!ok) {
      // Stay in 'preview' with permissionError set, so the host UI shows
      // the error overlay and a Cancel button. cancel() unwinds.
    }
  }, [acquireStream, phase]);

  const startRecording = useCallback(() => {
    const cameraStream = streamRef.current;
    const canvas = canvasRef.current;
    if (!cameraStream || !canvas || !supported) return;
    if (canvas.width === 0 || canvas.height === 0) return;

    // Composite stream: video from canvas (always well-formed) + audio
    // straight from the camera mic.
    const canvasStream = canvas.captureStream(CAPTURE_FPS);
    canvasStreamRef.current = canvasStream;
    const audioTracks = cameraStream.getAudioTracks();
    const recordingStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioTracks,
    ]);

    chunksRef.current = [];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(recordingStream, { mimeType: supported.mime });
    } catch {
      setPermissionError('Could not start the recorder.');
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      clearTick();
      const baseType = supported.mime.split(';')[0];
      const rawBlob = new Blob(chunksRef.current, { type: baseType });
      const durationMs = Math.max(1, Date.now() - startedAtRef.current);

      stopCanvasStream();

      if (rawBlob.size === 0) {
        setPermissionError(
          'No video data was captured. Try recording for a bit longer before pressing stop.',
        );
        stopStream();
        stopDrawLoop();
        setPhase('idle');
        return;
      }

      let finalBlob: Blob = rawBlob;
      if (baseType === 'video/webm') {
        try {
          finalBlob = await fixWebmDuration(rawBlob, durationMs, { logger: false });
        } catch {
          // fall back to raw blob — server-side ffmpeg can re-mux if needed
        }
      }

      const filename = `recording-${Date.now()}.${supported.ext}`;
      const file = new File([finalBlob], filename, { type: baseType });
      const url = URL.createObjectURL(finalBlob);
      recordedUrlRef.current = url;
      setRecordedFile(file);
      setRecordedUrl(url);
      setPhase('review');
      // Release the camera once the clip is captured; re-acquire only on retake.
      stopDrawLoop();
      stopStream();
    };

    setElapsedSeconds(0);
    startedAtRef.current = Date.now();
    recorder.start();
    setPhase('recording');
    tickRef.current = window.setInterval(() => {
      setElapsedSeconds((Date.now() - startedAtRef.current) / 1000);
    }, 200);
  }, [clearTick, stopCanvasStream, stopDrawLoop, stopStream, supported]);

  const stopRecording = useCallback(() => {
    try {
      recorderRef.current?.stop();
    } catch {
      // ignore
    }
  }, []);

  // Auto-stop at the soft cap
  useEffect(() => {
    if (phase !== 'recording') return;
    if (elapsedSeconds >= MAX_DURATION_SECONDS) {
      stopRecording();
    }
  }, [elapsedSeconds, phase, stopRecording]);

  const retake = useCallback(async () => {
    revokeRecordedUrl();
    setRecordedUrl(null);
    setRecordedFile(null);
    chunksRef.current = [];
    setElapsedSeconds(0);
    setPermissionError(null);
    setPhase('preview');
    await acquireStream();
  }, [acquireStream, revokeRecordedUrl]);

  const cancel = useCallback(() => {
    clearTick();
    stopDrawLoop();
    stopCanvasStream();
    try {
      recorderRef.current?.stop();
    } catch {
      // ignore
    }
    recorderRef.current = null;
    stopStream();
    revokeRecordedUrl();
    setRecordedUrl(null);
    setRecordedFile(null);
    chunksRef.current = [];
    setElapsedSeconds(0);
    setPermissionError(null);
    setPhase('idle');
  }, [clearTick, revokeRecordedUrl, stopCanvasStream, stopDrawLoop, stopStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTick();
      stopDrawLoop();
      stopCanvasStream();
      try {
        recorderRef.current?.stop();
      } catch {
        // ignore
      }
      recorderRef.current = null;
      stopStream();
      revokeRecordedUrl();
    };
  }, [clearTick, revokeRecordedUrl, stopCanvasStream, stopDrawLoop, stopStream]);

  return {
    phase,
    elapsedSeconds,
    recordedUrl,
    recordedFile,
    permissionError,
    isSupported,
    liveVideoRef,
    canvasRef,
    beginPreview,
    startRecording,
    stopRecording,
    retake,
    cancel,
  };
};
